import { useState, useEffect } from 'react';

const LenderDashboardPage = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading metrics
    setTimeout(() => {
      const today = new Date();
      const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
      const daysLeft = daysInMonth - today.getDate();
      
      setMetrics({
        // Portfolio Metrics
        wac: 6.847, // Weighted Average Coupon
        weightedAvgDTI: 38.2,
        avgLoanAmount: 487500,
        avgFICO: 742,
        
        // MTD Metrics
        submissionsMTD: 147,
        locksMTD: 98,
        fundingsMTD: 72,
        
        // Daily Metrics
        submissionsToday: 8,
        locksToday: 5,
        fundingsToday: 3,
        
        // Pipeline Metrics
        pullThroughRatio: 66.7, // (Fundings / Locks) * 100
        pipelineVolume: 142500000, // $142.5M
        activeBrokers: 24,
        avgDaysToClose: 28,
        
        // Calendar
        daysInMonth: daysInMonth,
        daysLeft: daysLeft,
        currentDay: today.getDate(),
        
        // Trends (last 7 days)
        dailySubmissions: [12, 15, 9, 14, 11, 13, 8],
        dailyLocks: [8, 10, 6, 9, 7, 8, 5],
        dailyFundings: [5, 4, 6, 3, 5, 4, 3],
        
        // Top Performers
        topBrokers: [
          { name: 'Prime Mortgage Group', volume: 28500000, loans: 42 },
          { name: 'Elite Lending Partners', volume: 22300000, loans: 35 },
          { name: 'First Choice Brokers', volume: 18700000, loans: 28 }
        ],
        
        // Status Breakdown
        statusBreakdown: {
          submitted: 45,
          inReview: 32,
          approved: 28,
          locked: 98,
          funded: 72
        },
        
        // Product Mix
        productMix: {
          conventional: 62,
          fha: 18,
          va: 12,
          jumbo: 8
        },
        
        // Loan Purpose
        loanPurpose: {
          purchase: 68,
          refinance: 32
        }
      });
      setLoading(false);
    }, 500);
  }, []);

  if (loading) {
    return (
      <div className="page-body">
        <div className="container-xl">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <div className="mt-2">Loading dashboard...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-body">
      <div className="container-xl">
        <div className="page-header d-print-none">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">Lender Dashboard</h2>
              <div className="text-secondary mt-1">
                Real-time portfolio metrics and performance indicators
              </div>
            </div>
            <div className="col-auto">
              <div className="btn-list">
                <button className="btn btn-primary">
                  <i className="ti ti-download me-1"></i>
                  Export Report
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Calendar Info */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="alert alert-info">
              <div className="d-flex align-items-center">
                <i className="ti ti-calendar me-2"></i>
                <div>
                  <strong>Month Progress:</strong> Day {metrics.currentDay} of {metrics.daysInMonth} • 
                  <strong className="ms-2">{metrics.daysLeft} days remaining</strong> • 
                  <span className="ms-2">{Math.round((metrics.currentDay / metrics.daysInMonth) * 100)}% complete</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Portfolio Metrics */}
        <div className="row row-deck row-cards mb-3">
          <div className="col-sm-6 col-lg-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="subheader">Weighted Avg Coupon (WAC)</div>
                  <div className="ms-auto">
                    <span className="text-green">
                      <i className="ti ti-trending-down"></i>
                    </span>
                  </div>
                </div>
                <div className="h1 mb-0">{metrics.wac.toFixed(3)}%</div>
                <div className="text-secondary small">Portfolio average rate</div>
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="subheader">Weighted Avg DTI</div>
                  <div className="ms-auto">
                    <span className="text-green">
                      <i className="ti ti-trending-down"></i>
                    </span>
                  </div>
                </div>
                <div className="h1 mb-0">{metrics.weightedAvgDTI.toFixed(1)}%</div>
                <div className="text-secondary small">Portfolio average DTI</div>
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="subheader">Avg Loan Amount</div>
                  <div className="ms-auto">
                    <span className="text-blue">
                      <i className="ti ti-trending-up"></i>
                    </span>
                  </div>
                </div>
                <div className="h1 mb-0">${(metrics.avgLoanAmount / 1000).toFixed(0)}K</div>
                <div className="text-secondary small">Average loan size</div>
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="subheader">Avg FICO Score</div>
                  <div className="ms-auto">
                    <span className="text-green">
                      <i className="ti ti-trending-up"></i>
                    </span>
                  </div>
                </div>
                <div className="h1 mb-0">{metrics.avgFICO}</div>
                <div className="text-secondary small">Portfolio average</div>
              </div>
            </div>
          </div>
        </div>

        {/* MTD Performance */}
        <div className="row row-deck row-cards mb-3">
          <div className="col-lg-4">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Submissions MTD</h3>
              </div>
              <div className="card-body">
                <div className="h1 text-primary mb-2">{metrics.submissionsMTD}</div>
                <div className="text-secondary mb-2">
                  Today: <strong className="text-blue">{metrics.submissionsToday}</strong>
                </div>
                <div className="progress progress-sm">
                  <div className="progress-bar bg-primary" style={{ width: '75%' }}></div>
                </div>
                <div className="text-secondary small mt-1">75% of monthly target (196)</div>
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Locks MTD</h3>
              </div>
              <div className="card-body">
                <div className="h1 text-success mb-2">{metrics.locksMTD}</div>
                <div className="text-secondary mb-2">
                  Today: <strong className="text-green">{metrics.locksToday}</strong>
                </div>
                <div className="progress progress-sm">
                  <div className="progress-bar bg-success" style={{ width: '70%' }}></div>
                </div>
                <div className="text-secondary small mt-1">70% of monthly target (140)</div>
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Fundings MTD</h3>
              </div>
              <div className="card-body">
                <div className="h1 text-cyan mb-2">{metrics.fundingsMTD}</div>
                <div className="text-secondary mb-2">
                  Today: <strong className="text-cyan">{metrics.fundingsToday}</strong>
                </div>
                <div className="progress progress-sm">
                  <div className="progress-bar bg-cyan" style={{ width: '72%' }}></div>
                </div>
                <div className="text-secondary small mt-1">72% of monthly target (100)</div>
              </div>
            </div>
          </div>
        </div>

        {/* Pipeline Health */}
        <div className="row row-deck row-cards mb-3">
          <div className="col-lg-8">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">7-Day Activity Trend</h3>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-12">
                    <div className="table-responsive">
                      <table className="table table-vcenter">
                        <thead>
                          <tr>
                            <th>Metric</th>
                            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => (
                              <th key={idx} className="text-center">{day}</th>
                            ))}
                            <th className="text-end">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="fw-bold">Submissions</td>
                            {metrics.dailySubmissions.map((val, idx) => (
                              <td key={idx} className="text-center">{val}</td>
                            ))}
                            <td className="text-end fw-bold">
                              {metrics.dailySubmissions.reduce((a, b) => a + b, 0)}
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Locks</td>
                            {metrics.dailyLocks.map((val, idx) => (
                              <td key={idx} className="text-center">{val}</td>
                            ))}
                            <td className="text-end fw-bold">
                              {metrics.dailyLocks.reduce((a, b) => a + b, 0)}
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Fundings</td>
                            {metrics.dailyFundings.map((val, idx) => (
                              <td key={idx} className="text-center">{val}</td>
                            ))}
                            <td className="text-end fw-bold">
                              {metrics.dailyFundings.reduce((a, b) => a + b, 0)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Pipeline Health</h3>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Pull-Through Ratio</span>
                    <span className="fw-bold text-success">{metrics.pullThroughRatio.toFixed(1)}%</span>
                  </div>
                  <div className="progress progress-sm">
                    <div className="progress-bar bg-success" style={{ width: `${metrics.pullThroughRatio}%` }}></div>
                  </div>
                  <div className="text-secondary small mt-1">
                    {metrics.fundingsMTD} funded / {metrics.locksMTD} locked
                  </div>
                </div>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Pipeline Volume</span>
                    <span className="fw-bold">${(metrics.pipelineVolume / 1000000).toFixed(1)}M</span>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Active Brokers</span>
                    <span className="fw-bold">{metrics.activeBrokers}</span>
                  </div>
                </div>
                <div>
                  <div className="d-flex justify-content-between mb-1">
                    <span>Avg Days to Close</span>
                    <span className="fw-bold">{metrics.avgDaysToClose} days</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Status Breakdown & Product Mix */}
        <div className="row row-deck row-cards mb-3">
          <div className="col-lg-6">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Pipeline Status</h3>
              </div>
              <div className="card-body">
                <div className="list-group list-group-flush">
                  <div className="list-group-item">
                    <div className="row align-items-center">
                      <div className="col">
                        <span className="badge bg-blue-lt me-2">Submitted</span>
                        New submissions under review
                      </div>
                      <div className="col-auto">
                        <span className="h3 mb-0">{metrics.statusBreakdown.submitted}</span>
                      </div>
                    </div>
                  </div>
                  <div className="list-group-item">
                    <div className="row align-items-center">
                      <div className="col">
                        <span className="badge bg-yellow-lt me-2">In Review</span>
                        Active underwriting
                      </div>
                      <div className="col-auto">
                        <span className="h3 mb-0">{metrics.statusBreakdown.inReview}</span>
                      </div>
                    </div>
                  </div>
                  <div className="list-group-item">
                    <div className="row align-items-center">
                      <div className="col">
                        <span className="badge bg-green-lt me-2">Approved</span>
                        Approved, pending lock
                      </div>
                      <div className="col-auto">
                        <span className="h3 mb-0">{metrics.statusBreakdown.approved}</span>
                      </div>
                    </div>
                  </div>
                  <div className="list-group-item">
                    <div className="row align-items-center">
                      <div className="col">
                        <span className="badge bg-purple-lt me-2">Locked</span>
                        Rate locked
                      </div>
                      <div className="col-auto">
                        <span className="h3 mb-0">{metrics.statusBreakdown.locked}</span>
                      </div>
                    </div>
                  </div>
                  <div className="list-group-item">
                    <div className="row align-items-center">
                      <div className="col">
                        <span className="badge bg-cyan-lt me-2">Funded</span>
                        Closed loans
                      </div>
                      <div className="col-auto">
                        <span className="h3 mb-0">{metrics.statusBreakdown.funded}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Product Mix & Purpose</h3>
              </div>
              <div className="card-body">
                <h4 className="mb-3">Product Type</h4>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Conventional</span>
                    <span className="fw-bold">{metrics.productMix.conventional}%</span>
                  </div>
                  <div className="progress progress-sm mb-2">
                    <div className="progress-bar bg-primary" style={{ width: `${metrics.productMix.conventional}%` }}></div>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>FHA</span>
                    <span className="fw-bold">{metrics.productMix.fha}%</span>
                  </div>
                  <div className="progress progress-sm mb-2">
                    <div className="progress-bar bg-success" style={{ width: `${metrics.productMix.fha}%` }}></div>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>VA</span>
                    <span className="fw-bold">{metrics.productMix.va}%</span>
                  </div>
                  <div className="progress progress-sm mb-2">
                    <div className="progress-bar bg-info" style={{ width: `${metrics.productMix.va}%` }}></div>
                  </div>
                </div>
                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Jumbo</span>
                    <span className="fw-bold">{metrics.productMix.jumbo}%</span>
                  </div>
                  <div className="progress progress-sm mb-2">
                    <div className="progress-bar bg-warning" style={{ width: `${metrics.productMix.jumbo}%` }}></div>
                  </div>
                </div>

                <h4 className="mb-3">Loan Purpose</h4>
                <div className="mb-2">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Purchase</span>
                    <span className="fw-bold">{metrics.loanPurpose.purchase}%</span>
                  </div>
                  <div className="progress progress-sm mb-2">
                    <div className="progress-bar bg-cyan" style={{ width: `${metrics.loanPurpose.purchase}%` }}></div>
                  </div>
                </div>
                <div>
                  <div className="d-flex justify-content-between mb-1">
                    <span>Refinance</span>
                    <span className="fw-bold">{metrics.loanPurpose.refinance}%</span>
                  </div>
                  <div className="progress progress-sm">
                    <div className="progress-bar bg-purple" style={{ width: `${metrics.loanPurpose.refinance}%` }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Performers */}
        <div className="row row-deck row-cards">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Top Performing Brokers MTD</h3>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-vcenter">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Broker</th>
                        <th className="text-end">Volume</th>
                        <th className="text-end">Loans</th>
                        <th className="text-end">Avg Loan Size</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.topBrokers.map((broker, idx) => (
                        <tr key={idx}>
                          <td>
                            <span className={`badge ${
                              idx === 0 ? 'bg-yellow' : 
                              idx === 1 ? 'bg-gray' : 'bg-orange'
                            }`}>
                              #{idx + 1}
                            </span>
                          </td>
                          <td className="fw-bold">{broker.name}</td>
                          <td className="text-end fw-bold text-primary">
                            ${(broker.volume / 1000000).toFixed(1)}M
                          </td>
                          <td className="text-end">{broker.loans}</td>
                          <td className="text-end">
                            ${(broker.volume / broker.loans / 1000).toFixed(0)}K
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LenderDashboardPage;
